import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { getCashBalance } from '~/domains/payments/utils/utils';
import {
  detectCombinedBalanceUsage,
  buildBalanceUpdatePayload,
  isDualBalancePayload,
} from '~/domains/payments/utils/balanceUtils';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { useContest } from '~/domains/contest/hooks/useContest';
import { Routes } from '~/domains/common/constants/routes';
import { usePromotionsContext } from '~/domains/promotions';
import { PromotionActionLocation } from '~/domains/promotions/constants/analytics-enums';
import { useOpenPromoTermsModal } from '~/domains/promotions/hooks/useOpenPromoTermsModal';
import { useContestVoucherValidation } from '~/domains/promotions/hooks/useContestVoucherValidation';
import {
  parseVoucherToCardProps,
  constructPromoCardProps,
} from '~/domains/promotions/utils/transformers';

import { CurrencyType } from '../../../services/enums/currency-type.enum';
import { AsideContentType } from '../../../game/domains/common/types/aside';
import type { FullContest } from '../../../services/interfaces/full-contest.interface';
import { useIsGamecenterEnabled } from '../../../gamecenter/domains/common/hooks/useIsGamecenterEnabled';

import type { EnterContestSource } from './ConnectedEnterContestModal';
import useCreateEntries from './useCreateEntries';

const REDIRECT_TIMEOUT = 3000;

const getRef = (router: ReturnType<typeof useRouter>, contest: FullContest) => {
  if (
    router.asPath.startsWith(
      Routes.gameshell({
        contestId: contest.id,
        sport: contest.league.sport,
        contestType: contest.contest_type,
      })
    )
  ) {
    return router.query.ref as string;
  }

  return router.pathname;
};

type AddEntriesModalProps = {
  contestId: string;
  onClose: () => void;
  onSuccess?: (entryIDs: string[]) => void;
  source?: EnterContestSource;
};

function useEnterContestModal({ contestId, onClose, onSuccess }: AddEntriesModalProps) {
  const { t } = useTranslation('contest');
  const [createdEntryCount, setCreatedEntryCount] = useState(0);
  const [payWithCredits, setPayWithCredits] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [currentLocation, setCurrentLocation] = useState(router.pathname);

  const { selectedVoucher, selectVoucher, clearSelectedBonus, removeVoucher } =
    usePromotionsContext();

  const handleVoucherRemove = useCallback(() => {
    if (selectedVoucher) selectVoucher(selectedVoucher, PromotionActionLocation.CONTEST_ENTRIES);
  }, [selectedVoucher, selectVoucher]);

  const { viewVoucherDetails } = useOpenPromoTermsModal({
    location: PromotionActionLocation.CONTEST_ENTRIES,
    selectedPromoId: selectedVoucher?.voucherId,
  });

  const {
    data: balance,
    isLoading: isBalanceLoading,
    refetch: refetchBalance,
  } = useWalletBalance();
  const { openModal, closeModal, disableClose, enableClose } = useContext(RootModalsContext);
  const closeTimerRef = useRef(null);
  const contest = useContest(contestId, !!contestId);

  // EDGE-29560: a voucher selected on a cheaper contest can go stale here — if its value
  // can't cover this contest's entry cost, don't show or apply it (both maxValue and
  // entry_fee are in cents).
  const isVoucherBelowEntryCost =
    !!selectedVoucher && contest.data != null && selectedVoucher.maxValue < contest.data.entry_fee;

  useEffect(() => {
    if (isVoucherBelowEntryCost) removeVoucher();
  }, [isVoucherBelowEntryCost, removeVoucher]);

  const promoCardProps = useMemo(
    () =>
      !selectedVoucher || isVoucherBelowEntryCost
        ? null
        : constructPromoCardProps(
            selectedVoucher,
            selectedVoucher?.voucherId,
            parseVoucherToCardProps,
            viewVoucherDetails,
            handleVoucherRemove
          ),
    [selectedVoucher, isVoucherBelowEntryCost, viewVoucherDetails, handleVoucherRemove]
  );

  // EDGE-32049: expose the voucher value as a number (dollars) so the entry-fee math in
  // EnterContestScreen reads the domain source of truth instead of re-parsing the formatted
  // display string in `promoCardProps.value` (which NaN'd on the thousands separator, e.g. "$1,000").
  // Non-zero only when a voucher is present AND covers this contest's entry cost (same guard as promoCardProps).
  const voucherValueInDollars =
    selectedVoucher && !isVoucherBelowEntryCost ? selectedVoucher.maxValue / 100 : 0;

  const entryFeeInDollars = (contest.data?.entry_fee ?? 0) / 100;
  const { voucherError, isValidatingVoucher } = useContestVoucherValidation({
    // EDGE-29560: don't validate (or toast about) a voucher we're hiding because it can't
    // cover this contest's entry cost.
    selectedVoucher: isVoucherBelowEntryCost ? null : selectedVoucher,
    contestId: contest.data?.id,
    league: contest.data?.league?.name,
    contestType: contest.data?.contest_type,
    entryAmountInDollars: entryFeeInDollars,
  });

  const {
    createEntriesErrorMessage: entryValidationErrorMessage,
    isCreateEntriesLoading,
    createEntries,
    isCreateEntriesReady: isEntryValidationReady,
    isCreateEntriesSuccess,
  } = useCreateEntries({
    isAfterDeadline: contest.data?.is_after_first_late_swap_deadline,
    isEntertainmentOnly: contest.data?.settings?.entertainmentOnlySettings?.enabled,
    entries: contest.data?.entries,
    isAllowedInLocation: contest.data?.is_allowed_in_location,
    status: contest.data?.status,
    onClose,
    contestId,
  });
  const { isEnabled } = useIsGamecenterEnabled(
    contest.data?.contest_type,
    contest.data?.league.sport
  );

  const isLoading = isBalanceLoading || contest.isLoading;
  const handleCreateSuccess = useCallback(
    async (
      entryIDs: string[],
      cashBalanceBefore: number,
      creditsBalanceBefore: number,
      paidWithCredits: boolean
    ) => {
      if (!contest.data) {
        return;
      }

      onSuccess?.(entryIDs);

      setCreatedEntryCount(entryIDs.length);
      const isEntertainmentOnly = contest.data.settings?.entertainmentOnlySettings?.enabled;

      closeModal();
      const ref = getRef(router, contest.data);
      const pushPath = (() => {
        if (!isEnabled) {
          return Routes.gameshellPicksheet({
            contestId: contest.data.id,
            contestType: contest.data.contest_type,
            sport: contest.data.league.sport,
            referrer: ref,
            ...(entryIDs.length > 1
              ? {
                  aside: AsideContentType.MY_ENTRIES,
                }
              : {
                  entryId: entryIDs[0],
                }),
          });
        }

        if (entryIDs.length > 1) {
          return Routes.gamecenterEntriesOverall(contest.data.id);
        }
        return Routes.gamecenterPicks(contest.data.id, {
          entryId: entryIDs[0],
        });
      })();

      disableClose();

      const autoPicksSettings = contest.data?.settings?.autoPicksSettings;
      const hasAutoPicksEnabled =
        autoPicksSettings?.maxAutoPicks === null || autoPicksSettings?.maxAutoPicks > 0;

      const handleEditAutoPicks = () => {
        closeModal();

        const availableStrategies = autoPicksSettings?.availableStrategies || [];
        const defaultStrategy = autoPicksSettings?.strategies?.[0] || availableStrategies[0]?.value;

        openModal({
          type: 'AutoPickEditSheet',
          contestId: contest.data.id,
          currentStrategy: defaultStrategy,
          availableStrategies,
          redirectTo: pushPath,
          title: t('gamecenter.modal.gamecenterAutoPickEditSheet.addTitle'),
          description: t('gamecenter.modal.gamecenterAutoPickEditSheet.addDescription'),
        });
      };

      const { data: updatedBalance } = await refetchBalance();
      const cashBalanceAfter = getCashBalance(updatedBalance);
      const creditsBalanceAfter = updatedBalance?.credits ?? 0;

      // Determine if both balances changed (combined balance used)
      const usedCombinedBalance = detectCombinedBalanceUsage(
        cashBalanceBefore,
        cashBalanceAfter,
        creditsBalanceBefore,
        creditsBalanceAfter,
        paidWithCredits
      );

      // Build modal props based on whether combined balance was used
      // Note: This function is intentionally not memoized because it's called only once
      // per success callback invocation and doesn't depend on reactive state.
      const getBalanceModalProps = () => {
        if (isEntertainmentOnly) {
          return {};
        }
        const payload = buildBalanceUpdatePayload(
          usedCombinedBalance,
          cashBalanceBefore,
          cashBalanceAfter,
          creditsBalanceBefore,
          creditsBalanceAfter,
          paidWithCredits
        );
        // Transform to modal prop names (uses "InDollars" suffix)
        if (isDualBalancePayload(payload)) {
          return {
            cashBalanceInDollars: payload.cashBalance,
            previousCashBalanceInDollars: payload.previousCashBalance,
            creditsBalanceInDollars: payload.creditsBalance,
            previousCreditsBalanceInDollars: payload.previousCreditsBalance,
          };
        }
        return {
          balanceInDollars: payload.newBalance,
          previousBalanceInDollars: payload.previousBalance,
          usedCredits: payload.usedCredits,
        };
      };
      const modalProps = getBalanceModalProps();

      // Persist props before clearing the selected bonus
      const persistedPromoCardProps = promoCardProps ? { ...promoCardProps } : undefined;
      clearSelectedBonus();

      openModal({
        type: 'EnterContestModalSuccess',
        entryCount: entryIDs.length,
        redirectTo: pushPath,
        ...modalProps,
        hasAutoPicksEnabled,
        contestId: contest.data.id,
        onEditAutoPicks: hasAutoPicksEnabled ? handleEditAutoPicks : undefined,
        promoCardProps: persistedPromoCardProps,
      });
    },
    [
      clearSelectedBonus,
      closeModal,
      contest.data,
      disableClose,
      isEnabled,
      onSuccess,
      openModal,
      promoCardProps,
      refetchBalance,
      router,
      t,
    ]
  );

  const handleCreateEntries = useCallback(
    async (entriesCount: number, payWithCreditsForEntry: boolean, zipCode?: string) => {
      try {
        disableClose();

        // Capture BOTH balances BEFORE the entry submission
        const cashBalanceBefore = getCashBalance(balance);
        const creditsBalanceBefore = balance?.credits ?? 0;

        await createEntries({
          contestId,
          entryFee: contest.data?.entry_fee || 0,
          entriesCount,
          zipCode,
          isEntertainmentOnly: contest.data?.settings?.entertainmentOnlySettings?.enabled,
          currencyType: payWithCreditsForEntry ? CurrencyType.VIRTUAL : CurrencyType.FIAT,
          callback: (entryIDs) => {
            // Pass both balance values to determine if combined balance was used
            handleCreateSuccess(
              entryIDs,
              cashBalanceBefore,
              creditsBalanceBefore,
              payWithCreditsForEntry
            );
            return Promise.resolve();
          },
        });
      } catch (e) {
        enableClose();
        console.error('Error creating entries', e);
      }
    },
    [
      balance,
      contest.data?.entry_fee,
      contest.data?.settings?.entertainmentOnlySettings?.enabled,
      contestId,
      createEntries,
      disableClose,
      enableClose,
      handleCreateSuccess,
    ]
  );

  const handleOpenDepositModal = useCallback(() => {
    openModal({
      type: 'DepositModal',
      contestIdForPostDeposit: contestId,
    });
  }, [contestId, openModal]);

  useEffect(
    () => () => {
      clearTimeout(closeTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!currentLocation) {
      setCurrentLocation(router.pathname);
    } else if (currentLocation !== router.pathname) {
      onClose();
    }
  }, [currentLocation, onClose, router.pathname]);

  // Auto-check "Use Credits" only when cash alone is insufficient but combined balance can cover
  const entryFee = (contest.data?.entry_fee ?? 0) / 100;
  const creditBalanceValue = balance?.credits ?? 0;
  const cashBalanceValue = getCashBalance(balance);
  const combinedBalance = cashBalanceValue + creditBalanceValue;
  const cashInsufficient = cashBalanceValue < entryFee;
  const combinedSufficient = combinedBalance >= entryFee && entryFee > 0;
  // Only auto-check when it's impossible to enter without using credits
  const shouldAutoCheckCredits = cashInsufficient && combinedSufficient && creditBalanceValue > 0;
  const hasAutoCheckedCredits = useRef(false);

  useEffect(() => {
    // Only auto-check once, not on subsequent renders
    if (hasAutoCheckedCredits.current) return;
    if (isBalanceLoading) return;

    if (shouldAutoCheckCredits) {
      setPayWithCredits(true);
      hasAutoCheckedCredits.current = true;
    }
  }, [shouldAutoCheckCredits, isBalanceLoading]);

  const state = useMemo(() => {
    if (isLoading) {
      return 'loading';
    }
    if (isCreateEntriesLoading) {
      return 'submitting';
    }
    if (isCreateEntriesSuccess) {
      return 'success';
    }

    return 'default';
  }, [isCreateEntriesLoading, isCreateEntriesSuccess, isLoading]);

  const isCreateEntriesReady = isEntryValidationReady && !voucherError && !isValidatingVoucher;

  return {
    state,
    handleOpenDepositModal,
    handleCreateEntries,
    nodeRef,
    createEntriesErrorMessage: entryValidationErrorMessage,
    voucherError,
    createdEntryCount,
    cashBalance: getCashBalance(balance),
    creditBalance: balance?.credits,
    payWithCredits,
    onPayWithCreditsChange: setPayWithCredits,
    contest,
    isCreateEntriesReady,
    promoCardProps,
    voucherValueInDollars,
  };
}

export default useEnterContestModal;

export { REDIRECT_TIMEOUT };
